import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  adminBookings, adminSetStatus,
  adminMailList, adminMailRead, adminMailDelete,
} from './bookingClient';
import { Wordmark } from './SiteChrome';
import { useAuth, signOut } from '../../AuthContext';
import './site.css';

// Owner dashboard (/admin): meetings grouped by day plus written inquiries
// and the hello@ inbox. Signed in through the kit login (the route is
// mounted with public:false, so the kit sends visitors to /login first); the
// backend (requireOwner — internal claim) is the security boundary, this
// page is just UI.

const fmtTime = (min) => {
  const h24 = Math.floor(min / 60);
  const h = ((h24 + 11) % 12) + 1;
  return `${h}:${String(min % 60).padStart(2, '0')} ${h24 < 12 ? 'AM' : 'PM'}`;
};
const fmtDate = (iso) =>
  new Date(`${iso}T12:00:00`).toLocaleDateString('en-US', {
    weekday: 'long', month: 'long', day: 'numeric',
  });

const STATUS_TONE = {
  pending: 'var(--ac-turq)',
  confirmed: 'var(--ac-ink)',
  completed: 'var(--ac-sea)',
  cancelled: 'var(--ac-danger)',
};

function MeetingCard({ b, onStatus, busy }) {
  return (
    <div className="ac-admin-card">
      <div className="ac-admin-row">
        <strong style={{ fontSize: 16 }}>
          {b.date ? `${fmtTime(b.startMin)} · ` : ''}{b.name} — {b.company}
        </strong>
        <span className="ac-admin-status" style={{ color: STATUS_TONE[b.status] }}>{b.status}</span>
      </div>
      {b.interests?.length > 0 && (
        <div className="ac-admin-row"><span>Interested in</span><strong>{b.interests.join(', ')}</strong></div>
      )}
      <div className="ac-admin-row">
        <span>Email</span>
        <strong><a href={`mailto:${b.email}`}>{b.email}</a></strong>
      </div>
      {b.site && (
        <div className="ac-admin-row">
          <span>Current site</span>
          <strong><a href={/^https?:/.test(b.site) ? b.site : `https://${b.site}`} target="_blank" rel="noreferrer">{b.site}</a></strong>
        </div>
      )}
      {(b.budget || b.timeline) && (
        <div className="ac-admin-row"><span>Budget · timeline</span><strong>{[b.budget, b.timeline].filter(Boolean).join(' · ')}</strong></div>
      )}
      {b.message && <div className="ac-admin-row"><span>Message</span><strong style={{ fontWeight: 400 }}>{b.message}</strong></div>}
      <div className="ac-admin-row ac-small ac-muted">
        <span>{b.code}</span>
        <span>{b.date ? `${b.durationMin} min` : 'written inquiry'}</span>
      </div>
      <div className="ac-admin-actions">
        {b.status === 'pending' && (
          <button className="ac-btn ac-btn-sm" disabled={busy} onClick={() => onStatus(b.id, 'confirmed')}>Confirm</button>
        )}
        {(b.status === 'pending' || b.status === 'confirmed') && (
          <>
            <button className="ac-btn ac-btn-ghost ac-btn-sm" disabled={busy} onClick={() => onStatus(b.id, 'completed')}>Complete</button>
            <button className="ac-btn ac-btn-ghost ac-btn-sm" disabled={busy} onClick={() => onStatus(b.id, 'cancelled')}>Cancel</button>
          </>
        )}
        {b.status === 'cancelled' && (
          <button className="ac-btn ac-btn-ghost ac-btn-sm" disabled={busy} onClick={() => onStatus(b.id, 'pending')}>Restore</button>
        )}
      </div>
    </div>
  );
}

// Inbox for hello@anastasia.dev — SES receiving drops raw mail in S3, the
// backend parses it (backend/api/mailInbox.js), this view reads it.
function MailCard({ m, onDeleted }) {
  const [openBody, setOpenBody] = useState(null); // null | 'loading' | message
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  async function toggle() {
    if (openBody && openBody !== 'loading') return setOpenBody(null);
    setOpenBody('loading');
    try {
      setOpenBody(await adminMailRead(m.key));
    } catch (err) {
      setError(err.message);
      setOpenBody(null);
    }
  }

  async function remove() {
    setBusy(true);
    try {
      await adminMailDelete(m.key);
      onDeleted(m.key);
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  }

  const when = new Date(m.date).toLocaleString('en-US', {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  });
  return (
    <div className="ac-admin-card">
      <div className="ac-admin-row" style={{ cursor: 'pointer' }} onClick={toggle} role="button" tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter') toggle(); }}>
        <strong style={{ fontSize: 15 }}>{m.subject}</strong>
        <span className="ac-muted ac-small">{when}</span>
      </div>
      <div className="ac-admin-row ac-small">
        <span className="ac-muted">From</span>
        <strong>{m.from}</strong>
      </div>
      {!openBody && m.snippet && (
        <p className="ac-muted ac-small" style={{ margin: 0 }}>{m.snippet}…</p>
      )}
      {openBody === 'loading' && <p className="ac-muted ac-small" style={{ margin: 0 }}>Opening…</p>}
      {openBody && openBody !== 'loading' && (
        <div style={{ display: 'grid', gap: 10 }}>
          <p style={{ margin: 0, whiteSpace: 'pre-wrap', fontSize: 14, lineHeight: 1.55 }}>
            {openBody.text || '(no plain-text body)'}
          </p>
          {openBody.attachments?.length > 0 && (
            <p className="ac-muted ac-small" style={{ margin: 0 }}>
              Attachments: {openBody.attachments.map((a) => a.filename).join(', ')}
            </p>
          )}
        </div>
      )}
      {error && <p className="ac-error ac-small" role="alert">{error}</p>}
      <div className="ac-admin-actions">
        <a className="ac-btn ac-btn-sm" href={`mailto:${m.from.replace(/^.*<|>$/g, '')}?subject=${encodeURIComponent(`Re: ${m.subject}`)}`}>
          Reply
        </a>
        <button className="ac-btn ac-btn-ghost ac-btn-sm" onClick={toggle}>
          {openBody && openBody !== 'loading' ? 'Collapse' : 'Read'}
        </button>
        <button className="ac-btn ac-btn-ghost ac-btn-sm" disabled={busy} onClick={remove}>Delete</button>
      </div>
    </div>
  );
}

function Inbox() {
  const [mail, setMail] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setError('');
    try {
      const { messages } = await adminMailList();
      setMail(messages);
    } catch (err) {
      setError(err.message);
      setMail([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div style={{ display: 'grid', gap: 14 }}>
      <div className="ac-admin-row">
        <span className="ac-muted ac-small">hello@anastasia.dev</span>
        <button className="ac-chip" onClick={load}>Refresh</button>
      </div>
      {error && <p className="ac-error" role="alert">{error}</p>}
      {!mail ? (
        <p className="ac-muted">Checking the mailbox…</p>
      ) : mail.length === 0 ? (
        <p className="ac-muted">No mail yet — messages to hello@anastasia.dev land here.</p>
      ) : (
        mail.map((m) => (
          <MailCard key={m.key} m={m}
            onDeleted={(key) => setMail((list) => list.filter((x) => x.key !== key))} />
        ))
      )}
    </div>
  );
}

export default function AdminMeetings() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [view, setView] = useState('upcoming'); // upcoming | inquiries | past | inbox

  const load = useCallback(async () => {
    setError('');
    try {
      setData(await adminBookings());
    } catch (err) {
      setError(err.message);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function setStatus(id, status) {
    setBusy(true);
    try {
      await adminSetStatus(id, status);
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  const bookings = data?.bookings || [];
  const today = data?.today || '';
  const meetings = bookings.filter((b) => b.date);
  const inquiries = bookings.filter((b) => !b.date).sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
  const upcoming = meetings.filter((b) => b.date >= today);
  const past = meetings.filter((b) => b.date < today).reverse();
  const shown = view === 'past' ? past : view === 'inquiries' ? inquiries : upcoming;
  const byDate = shown.reduce((m, b) => {
    const key = b.date || 'inquiries';
    (m[key] = m[key] || []).push(b);
    return m;
  }, {});

  const VIEWS = [
    ['upcoming', `Upcoming (${upcoming.length})`],
    ['inquiries', `Inquiries (${inquiries.length})`],
    ['past', `Past (${past.length})`],
    ['inbox', 'Inbox'],
  ];

  return (
    <div className="ac" style={{ minHeight: '100vh' }}>
      <nav className="ac-nav scrolled" aria-label="Site">
        <div className="ac-wrap ac-nav-inner">
          <Link className="ac-nav-brand" to="/"><Wordmark /></Link>
          <span className="ac-muted" style={{ marginLeft: 'auto', marginRight: 12, fontSize: 13 }}>{user?.email}</span>
          <button className="ac-btn ac-btn-ghost ac-btn-sm" onClick={() => signOut()}>
            Sign out
          </button>
        </div>
      </nav>

      <main id="main" className="ac-section" style={{ paddingTop: 32 }}>
        <div className="ac-wrap" style={{ maxWidth: 760 }}>
          {(
            <div style={{ display: 'grid', gap: 26 }}>
              <div>
                <span className="ac-kicker">The book</span>
                <h2>
                  {view === 'past' ? 'Past meetings'
                    : view === 'inquiries' ? 'Written inquiries'
                    : view === 'inbox' ? 'The mailbox'
                    : 'Upcoming meetings'}
                </h2>
                <div className="ac-chips" style={{ marginTop: 18 }}>
                  {VIEWS.map(([id, label]) => (
                    <button key={id} className={`ac-chip ${view === id ? 'on' : ''}`} onClick={() => setView(id)}>{label}</button>
                  ))}
                  <button className="ac-chip" onClick={() => load()}>Refresh</button>
                </div>
              </div>
              {error && <p className="ac-error" role="alert">{error}</p>}
              {view === 'inbox' ? (
                <Inbox />
              ) : !data ? (
                <p className="ac-muted">Opening the book…</p>
              ) : shown.length === 0 ? (
                <p className="ac-muted">
                  {view === 'past' ? 'No past meetings yet.'
                    : view === 'inquiries' ? 'No written inquiries yet.'
                    : 'No upcoming meetings — the calendar is clear.'}
                </p>
              ) : (
                Object.entries(byDate).map(([date, list]) => (
                  <div key={date} style={{ display: 'grid', gap: 14 }}>
                    <h3 style={{ borderBottom: '1px solid var(--ac-hairline)', paddingBottom: 8 }}>
                      {date === 'inquiries' ? 'Newest first' : fmtDate(date)}
                      <span className="ac-muted ac-small" style={{ marginLeft: 12 }}>
                        {list.length} {date === 'inquiries' ? 'inquir' + (list.length > 1 ? 'ies' : 'y') : 'meeting' + (list.length > 1 ? 's' : '')}
                      </span>
                    </h3>
                    {list.map((b) => (
                      <MeetingCard key={b.id} b={b} onStatus={setStatus} busy={busy} />
                    ))}
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
